import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Chip,
  LinearProgress,
  Divider,
  Avatar,
  Grid,
} from '@mui/material';
import {
  Badge,
  Person,
  Logout,
  PlayArrow,
  Pause,
  CheckCircle,
  Timer,
  Factory,
  Assignment,
  AccessTime,
  Warning,
} from '@mui/icons-material';

const API = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';
const INACTIVITY_MINUTES = 30;

// ─── Types ────────────────────────────────────────────────────────────────────

interface WorkCenter {
  id: string;
  code: string;
  name: string;
}

interface OperatorSession {
  sessionId: string;
  operatorId: string;
  operatorCode: string;
  operatorName: string;
  workCenterId: string;
  workCenterCode: string;
  workCenterName: string;
  isPrimaryWorkstation: boolean;
  loginTime: string;
  token: string | null;
}

interface WorkOrder {
  id: string;
  orderNumber: string;
  productCode: string;
  productName: string;
  operationDescription: string;
  workCenter: string;
  plannedQty: number;
  producedQty: number;
  scrapQty: number;
  reworkQty: number;
  status: 'queued' | 'started' | 'in_progress' | 'paused' | 'completed' | 'on_hold';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  startDate: string;
  dueDate: string;
  estimatedHours: number;
  startedAt?: string | null;
  notes?: string;
}

// ─── Utilities ────────────────────────────────────────────────────────────────

function authHeaders(token: string | null) {
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function formatTime(seconds: number) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return [h, m, s].map((v) => String(v).padStart(2, '0')).join(':');
}

function formatDue(dateStr: string): { label: string; color: string } {
  if (!dateStr) return { label: '—', color: 'text.secondary' };
  const diff = new Date(dateStr).getTime() - Date.now();
  const hrs = Math.round(diff / 3600000);
  if (hrs < 0) return { label: `${Math.abs(hrs)}h overdue`, color: 'error.main' };
  if (hrs < 8) return { label: `Due in ${hrs}h`, color: 'warning.main' };
  return { label: new Date(dateStr).toLocaleDateString(), color: 'text.secondary' };
}

function priorityColor(p: WorkOrder['priority']): 'error' | 'warning' | 'success' | 'default' {
  if (p === 'urgent' || p === 'high') return 'error';
  if (p === 'medium') return 'warning';
  return 'default';
}

function normalizeStatus(status: string): WorkOrder['status'] {
  if (status === 'in_progress') return 'started';
  return status as WorkOrder['status'];
}

// ─── Clock ────────────────────────────────────────────────────────────────────

const Clock: React.FC<{ color?: string }> = ({ color = 'inherit' }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <Typography variant="h5" fontFamily="monospace" fontWeight={700} color={color}>
      {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
    </Typography>
  );
};

// ─── Login Screen ─────────────────────────────────────────────────────────────

interface LoginScreenProps {
  onLogin: (session: OperatorSession) => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onLogin }) => {
  const [badgeCode, setBadgeCode] = useState('');
  const [workCenters, setWorkCenters] = useState<WorkCenter[]>([]);
  const [workCenterCode, setWorkCenterCode] = useState('');
  const [wcLoading, setWcLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const badgeRef = useRef<HTMLInputElement>(null);

  // Load all work centers on mount
  useEffect(() => {
    fetchWorkCenters();
    badgeRef.current?.focus();
  }, []); // eslint-disable-line

  const fetchWorkCenters = async (badge?: string) => {
    setWcLoading(true);
    try {
      const url = badge
        ? `${API}/shopfloor/workcenters?badgeCode=${encodeURIComponent(badge)}`
        : `${API}/shopfloor/workcenters`;
      const r = await fetch(url);
      const body = await r.json();
      const list: WorkCenter[] = Array.isArray(body) ? body : (body?.data ?? []);
      setWorkCenters(list);
      if (list.length > 0) setWorkCenterCode(list[0].code);
    } catch {
      setWorkCenters([]);
    } finally {
      setWcLoading(false);
    }
  };

  const handleBadgeBlur = () => {
    if (badgeCode.trim()) fetchWorkCenters(badgeCode.trim());
  };

  const handleLogin = async () => {
    if (!badgeCode.trim()) { setError('Please scan or enter your badge code'); return; }
    if (!workCenterCode.trim()) { setError('Please select a work center'); return; }
    setError('');
    setLoading(true);
    try {
      const r = await fetch(`${API}/shopfloor/operators/badge-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ badgeCode: badgeCode.trim(), workCenterCode: workCenterCode.trim() }),
      });
      const body = await r.json();
      if (!r.ok || !body.success) {
        setError(body.message || 'Login failed');
        return;
      }
      onLogin(body.data as OperatorSession);
    } catch (e: any) {
      setError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleLogin();
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      sx={{ bgcolor: '#0d1b2a' }}
    >
      {/* Clock */}
      <Box mb={4}>
        <Clock color="#90caf9" />
        <Typography textAlign="center" color="#90caf9" variant="body2">
          {new Date().toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
      </Box>

      <Card sx={{ maxWidth: 500, width: '100%', mx: 2, borderRadius: 3 }}>
        <CardContent sx={{ p: 5 }}>
          <Box textAlign="center" mb={4}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 72, height: 72, mx: 'auto', mb: 2 }}>
              <Factory sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h4" fontWeight={800}>
              Operator Terminal
            </Typography>
            <Typography color="text.secondary" mt={1}>
              Scan your badge or enter Employee ID
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <TextField
            inputRef={badgeRef}
            label="Badge Code / Employee ID"
            value={badgeCode}
            onChange={(e) => setBadgeCode(e.target.value)}
            onKeyDown={handleKey}
            onBlur={handleBadgeBlur}
            fullWidth
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Badge />
                </InputAdornment>
              ),
              sx: { fontSize: '1.2rem', py: 0.5 },
            }}
            placeholder="e.g. EMP-003"
            sx={{ mb: 3 }}
          />

          <FormControl fullWidth sx={{ mb: 4 }}>
            <InputLabel>Work Center</InputLabel>
            {workCenters.length > 0 ? (
              <Select
                label="Work Center"
                value={workCenterCode}
                onChange={(e: SelectChangeEvent) => setWorkCenterCode(e.target.value)}
                disabled={wcLoading}
                sx={{ fontSize: '1.1rem' }}
              >
                {workCenters.map((wc) => (
                  <MenuItem key={wc.code} value={wc.code}>
                    {wc.code} — {wc.name}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <TextField
                label="Work Center Code"
                value={workCenterCode}
                onChange={(e) => setWorkCenterCode(e.target.value)}
                placeholder="e.g. WC-001"
                fullWidth
              />
            )}
          </FormControl>

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleLogin}
            disabled={loading || wcLoading}
            sx={{ py: 2, fontSize: '1.2rem', fontWeight: 700, borderRadius: 2 }}
          >
            {loading ? <CircularProgress size={26} color="inherit" /> : 'LOG IN TO WORKSTATION'}
          </Button>
        </CardContent>
      </Card>

      <Typography color="#546e7a" variant="caption" mt={4}>
        For badge issues, contact your supervisor
      </Typography>
    </Box>
  );
};

// ─── Work Order Card ──────────────────────────────────────────────────────────

interface WorkOrderCardProps {
  wo: WorkOrder;
  isActive: boolean;
  onClick: () => void;
}

const WorkOrderCard: React.FC<WorkOrderCardProps> = ({ wo, isActive, onClick }) => {
  const due = formatDue(wo.dueDate);
  const progress = wo.plannedQty > 0 ? Math.min((wo.producedQty / wo.plannedQty) * 100, 100) : 0;
  const status = normalizeStatus(wo.status);

  return (
    <Card
      onClick={onClick}
      sx={{
        mb: 1.5,
        cursor: 'pointer',
        border: '2px solid',
        borderColor: isActive ? 'primary.main' : 'transparent',
        bgcolor: isActive ? 'primary.50' : 'background.paper',
        transition: 'all 0.15s',
        '&:hover': { borderColor: 'primary.light', transform: 'translateY(-1px)', boxShadow: 4 },
      }}
    >
      <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem' }}>
            {wo.orderNumber}
          </Typography>
          <Box display="flex" gap={0.5}>
            <Chip label={wo.priority.toUpperCase()} color={priorityColor(wo.priority)} size="small" variant="outlined" />
            <Chip
              label={status === 'started' ? 'IN PROGRESS' : status.toUpperCase().replace('_', ' ')}
              color={status === 'started' ? 'warning' : status === 'paused' ? 'info' : status === 'completed' ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </Box>
        <Typography variant="body2" fontWeight={500} noWrap>
          {wo.productName}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5} mt={0.5}>
          <AccessTime sx={{ fontSize: 12 }} />
          {due.label}
          &nbsp;·&nbsp; {wo.producedQty}/{wo.plannedQty} units
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          color={status === 'completed' ? 'success' : 'primary'}
          sx={{ mt: 1, height: 6, borderRadius: 3 }}
        />
        {isActive && (
          <Typography variant="caption" color="primary.main" fontWeight={700} display="block" mt={0.5}>
            ← SELECTED
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Active Operation Panel ───────────────────────────────────────────────────

interface ActiveOpProps {
  wo: WorkOrder;
  token: string | null;
  onStatusChange: (woId: string, newStatus: WorkOrder['status'], qty?: Partial<WorkOrder>) => void;
}

const ActiveOperation: React.FC<ActiveOpProps> = ({ wo, token, onStatusChange }) => {
  const [producedQty, setProducedQty] = useState(wo.producedQty);
  const [scrapQty, setScrapQty] = useState(wo.scrapQty);
  const [reworkQty, setReworkQty] = useState(wo.reworkQty);
  const [notes, setNotes] = useState(wo.notes || '');
  const [elapsed, setElapsed] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const status = normalizeStatus(wo.status);

  // Sync qty when work order changes
  useEffect(() => {
    setProducedQty(wo.producedQty);
    setScrapQty(wo.scrapQty);
    setReworkQty(wo.reworkQty);
    setNotes(wo.notes || '');
    setConfirmComplete(false);
    setElapsed(0);
    setTimerRunning(status === 'started');
  }, [wo.id]); // eslint-disable-line

  useEffect(() => {
    if (!timerRunning) return;
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [timerRunning]);

  const progress = wo.plannedQty > 0 ? Math.min((producedQty / wo.plannedQty) * 100, 100) : 0;

  const apiCall = useCallback(
    async (method: string, path: string, body?: object) => {
      setSaving(true);
      setSaveMsg('');
      try {
        const r = await fetch(`${API}${path}`, {
          method,
          headers: authHeaders(token),
          ...(body ? { body: JSON.stringify(body) } : {}),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return await r.json();
      } finally {
        setSaving(false);
      }
    },
    [token]
  );

  const handleStart = async () => {
    try {
      await apiCall('POST', `/shopfloor/workorders/${wo.id}/start`);
      setTimerRunning(true);
      setSaveMsg('Started');
      onStatusChange(wo.id, 'started');
    } catch (e: any) { setSaveMsg('Error: ' + e.message); }
  };

  const handlePause = async () => {
    try {
      await apiCall('POST', `/shopfloor/workorders/${wo.id}/pause`);
      setTimerRunning(false);
      setSaveMsg('Paused');
      onStatusChange(wo.id, 'paused');
    } catch (e: any) { setSaveMsg('Error: ' + e.message); }
  };

  const handleSave = async () => {
    try {
      await apiCall('PUT', `/shopfloor/workorders/${wo.id}/progress`, {
        producedQty, scrapQty, reworkQty, notes,
      });
      setSaveMsg('Saved');
      onStatusChange(wo.id, status, { producedQty, scrapQty, reworkQty });
    } catch (e: any) { setSaveMsg('Error: ' + e.message); }
  };

  const handleComplete = async () => {
    if (!confirmComplete) { setConfirmComplete(true); return; }
    try {
      await apiCall('PUT', `/shopfloor/workorders/${wo.id}/progress`, {
        producedQty, scrapQty, reworkQty, notes, status: 'completed',
      });
      setTimerRunning(false);
      setSaveMsg('Completed!');
      onStatusChange(wo.id, 'completed', { producedQty, scrapQty, reworkQty });
      setConfirmComplete(false);
    } catch (e: any) { setSaveMsg('Error: ' + e.message); }
  };

  const isStarted = status === 'started';
  const isPaused = status === 'paused';
  const isQueued = status === 'queued' || status === 'on_hold';
  const isDone = status === 'completed';

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box flex={1} mr={2}>
            <Typography variant="h5" fontWeight={800}>
              {wo.orderNumber}
            </Typography>
            <Typography variant="body1" fontWeight={500} color="text.secondary">
              {wo.productName}
            </Typography>
            <Box display="flex" gap={1} mt={1}>
              <Chip label={wo.priority.toUpperCase()} color={priorityColor(wo.priority)} size="small" variant="outlined" />
              <Chip
                label={isStarted ? 'IN PROGRESS' : status.toUpperCase().replace('_', ' ')}
                color={isStarted ? 'warning' : isPaused ? 'info' : isDone ? 'success' : 'default'}
                size="small"
              />
            </Box>
          </Box>

          {/* Timer */}
          <Box
            textAlign="center"
            sx={{
              bgcolor: isStarted ? 'warning.light' : 'grey.100',
              borderRadius: 3,
              px: 3,
              py: 2,
              minWidth: 150,
              transition: 'background-color 0.3s',
            }}
          >
            <Timer sx={{ fontSize: 28, color: isStarted ? 'warning.dark' : 'action.disabled' }} />
            <Typography
              variant="h4"
              fontFamily="monospace"
              fontWeight={800}
              color={isStarted ? 'warning.dark' : 'text.secondary'}
            >
              {formatTime(elapsed)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Session Time
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Quantity Inputs */}
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Production Quantities
        </Typography>
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6} sm={3}>
            <Box textAlign="center" p={2} bgcolor="info.50" borderRadius={2} border="1px solid" borderColor="info.200">
              <Typography variant="caption" color="text.secondary" display="block">
                Planned
              </Typography>
              <Typography variant="h4" color="info.main" fontWeight={800}>
                {wo.plannedQty}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              label="Produced ✓"
              type="number"
              value={producedQty}
              onChange={(e) => setProducedQty(Math.max(0, Number(e.target.value)))}
              fullWidth
              inputProps={{ min: 0, style: { fontSize: '1.4rem', fontWeight: 700, textAlign: 'center' } }}
              sx={{ '& .MuiInputBase-root': { bgcolor: 'success.50' } }}
            />
            <LinearProgress
              variant="determinate"
              value={progress}
              color="success"
              sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
            />
            <Typography variant="caption" color="text.secondary">
              {progress.toFixed(0)}%
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              label="Scrap ✗"
              type="number"
              value={scrapQty}
              onChange={(e) => setScrapQty(Math.max(0, Number(e.target.value)))}
              fullWidth
              inputProps={{ min: 0, style: { fontSize: '1.4rem', fontWeight: 700, textAlign: 'center' } }}
              sx={{ '& .MuiInputBase-root': { bgcolor: 'error.50' } }}
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              label="Rework ↺"
              type="number"
              value={reworkQty}
              onChange={(e) => setReworkQty(Math.max(0, Number(e.target.value)))}
              fullWidth
              inputProps={{ min: 0, style: { fontSize: '1.4rem', fontWeight: 700, textAlign: 'center' } }}
              sx={{ '& .MuiInputBase-root': { bgcolor: 'warning.50' } }}
            />
          </Grid>
        </Grid>

        <TextField
          label="Notes / Issues"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          rows={2}
          size="small"
          placeholder="Any issues, deviations, or observations..."
          sx={{ mb: 3 }}
        />

        {saveMsg && (
          <Alert
            severity={saveMsg.startsWith('Error') ? 'error' : saveMsg === 'Completed!' ? 'success' : 'info'}
            sx={{ mb: 2 }}
            onClose={() => setSaveMsg('')}
          >
            {saveMsg}
          </Alert>
        )}

        {confirmComplete && (
          <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
            Confirm completion: Produced {producedQty}, Scrap {scrapQty}, Rework {reworkQty}. Click COMPLETE again to confirm.
          </Alert>
        )}

        {isDone ? (
          <Alert severity="success" icon={<CheckCircle />}>
            This work order is complete. Select another from the queue.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {(isQueued || isPaused) && (
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  color="success"
                  fullWidth
                  size="large"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
                  onClick={handleStart}
                  disabled={saving}
                  sx={{ py: 2.5, fontSize: '1.1rem', fontWeight: 700 }}
                >
                  START
                </Button>
              </Grid>
            )}
            {isStarted && (
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  color="warning"
                  fullWidth
                  size="large"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Pause />}
                  onClick={handlePause}
                  disabled={saving}
                  sx={{ py: 2.5, fontSize: '1.1rem', fontWeight: 700 }}
                >
                  PAUSE
                </Button>
              </Grid>
            )}
            {(isStarted || isPaused) && (
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  size="large"
                  onClick={handleSave}
                  disabled={saving}
                  sx={{ py: 2.5, fontSize: '1.1rem', fontWeight: 700 }}
                >
                  {saving ? <CircularProgress size={20} /> : 'SAVE'}
                </Button>
              </Grid>
            )}
            {(isStarted || isPaused) && (
              <Grid item xs={12} sm={4}>
                <Button
                  variant="contained"
                  color={confirmComplete ? 'error' : 'primary'}
                  fullWidth
                  size="large"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                  onClick={handleComplete}
                  disabled={saving}
                  sx={{ py: 2.5, fontSize: '1.1rem', fontWeight: 700 }}
                >
                  {confirmComplete ? 'CONFIRM COMPLETE' : 'COMPLETE'}
                </Button>
              </Grid>
            )}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Main Terminal ─────────────────────────────────────────────────────────────

const WorkstationTerminal: React.FC = () => {
  const [session, setSession] = useState<OperatorSession | null>(null);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [activeWo, setActiveWo] = useState<WorkOrder | null>(null);
  const [woLoading, setWoLoading] = useState(false);
  const [inactiveSeconds, setInactiveSeconds] = useState(0);
  const inactivityRef = useRef<NodeJS.Timeout | null>(null);

  // ── Inactivity timer ──
  const resetInactivity = useCallback(() => setInactiveSeconds(0), []);

  useEffect(() => {
    if (!session) return;
    const events = ['mousemove', 'keydown', 'click', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, resetInactivity));
    inactivityRef.current = setInterval(() => {
      setInactiveSeconds((s) => {
        if (s + 1 >= INACTIVITY_MINUTES * 60) {
          handleLogout();
          return 0;
        }
        return s + 1;
      });
    }, 1000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetInactivity));
      if (inactivityRef.current) clearInterval(inactivityRef.current);
    };
  }, [session]); // eslint-disable-line

  // ── Fetch work orders after login ──
  useEffect(() => {
    if (!session) return;
    loadWorkOrders();
  }, [session]); // eslint-disable-line

  const loadWorkOrders = async () => {
    if (!session) return;
    setWoLoading(true);
    try {
      const r = await fetch(
        `${API}/shopfloor/workorders?workCenterId=${encodeURIComponent(session.workCenterId)}`,
        { headers: authHeaders(session.token) }
      );
      const body = await r.json();
      const list: WorkOrder[] = Array.isArray(body) ? body : (body?.data ?? []);
      setWorkOrders(list);
      // Auto-select first in-progress order
      const inProgress = list.find((w) => normalizeStatus(w.status) === 'started' || normalizeStatus(w.status) === 'paused');
      if (inProgress) setActiveWo(inProgress);
    } catch {
      setWorkOrders([]);
    } finally {
      setWoLoading(false);
    }
  };

  const handleLogin = (s: OperatorSession) => {
    setSession(s);
    setInactiveSeconds(0);
  };

  const handleLogout = () => {
    setSession(null);
    setWorkOrders([]);
    setActiveWo(null);
    setInactiveSeconds(0);
  };

  const handleStatusChange = (woId: string, newStatus: WorkOrder['status'], qty?: Partial<WorkOrder>) => {
    setWorkOrders((prev) =>
      prev.map((w) =>
        w.id === woId ? { ...w, status: newStatus, ...(qty || {}) } : w
      )
    );
    setActiveWo((prev) =>
      prev && prev.id === woId ? { ...prev, status: newStatus, ...(qty || {}) } : prev
    );
  };

  const inactiveWarning = inactiveSeconds > (INACTIVITY_MINUTES - 2) * 60;
  const inactiveRemaining = INACTIVITY_MINUTES * 60 - inactiveSeconds;

  // ── Login screen ──
  if (!session) return <LoginScreen onLogin={handleLogin} />;

  const completedCount = workOrders.filter((w) => normalizeStatus(w.status) === 'completed').length;
  const inProgressCount = workOrders.filter(
    (w) => normalizeStatus(w.status) === 'started' || normalizeStatus(w.status) === 'paused'
  ).length;

  return (
    <Box minHeight="100vh" display="flex" flexDirection="column" sx={{ bgcolor: '#f0f4f8' }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: '#0d1b2a',
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: 4,
        }}
      >
        {/* Operator info */}
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
            <Person />
          </Avatar>
          <Box>
            <Typography color="white" fontWeight={700} variant="h6">
              {session.operatorName}
            </Typography>
            <Typography color="#90caf9" variant="body2">
              {session.operatorCode} · {session.workCenterName}
              {session.isPrimaryWorkstation && (
                <Chip label="PRIMARY" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: 10 }} />
              )}
            </Typography>
          </Box>
        </Box>

        {/* Stats + Clock */}
        <Box display="flex" alignItems="center" gap={3}>
          <Box display="flex" gap={1}>
            <Chip icon={<Factory />} label={`${inProgressCount} Active`} color="warning" size="small" />
            <Chip icon={<CheckCircle />} label={`${completedCount} Done`} color="success" size="small" />
          </Box>
          <Clock color="white" />
        </Box>

        {/* Inactivity + Logout */}
        <Box display="flex" alignItems="center" gap={2}>
          {inactiveWarning && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Warning sx={{ color: 'orange', fontSize: 18 }} />
              <Typography color="orange" variant="caption" fontWeight={700}>
                Auto-logout in {Math.ceil(inactiveRemaining)}s
              </Typography>
            </Box>
          )}
          <Button
            variant="outlined"
            color="error"
            startIcon={<Logout />}
            onClick={handleLogout}
            sx={{ color: '#ef9a9a', borderColor: '#ef9a9a', '&:hover': { bgcolor: 'rgba(239,154,154,0.1)' } }}
          >
            Logout
          </Button>
        </Box>
      </Box>

      {/* Main content */}
      <Box display="flex" flex={1} gap={0} overflow="hidden">
        {/* Work Order Queue */}
        <Box
          sx={{
            width: 360,
            flexShrink: 0,
            bgcolor: 'white',
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: '#1a237e',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Assignment sx={{ color: 'white', fontSize: 20 }} />
              <Typography color="white" fontWeight={700}>
                Work Queue ({workOrders.filter((w) => normalizeStatus(w.status) !== 'completed').length})
              </Typography>
            </Box>
            <Button
              size="small"
              onClick={loadWorkOrders}
              disabled={woLoading}
              sx={{ color: '#90caf9', minWidth: 0, fontSize: 12 }}
            >
              {woLoading ? <CircularProgress size={14} color="inherit" /> : 'Refresh'}
            </Button>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
            {woLoading && workOrders.length === 0 ? (
              <Box display="flex" justifyContent="center" pt={4}>
                <CircularProgress />
              </Box>
            ) : workOrders.length === 0 ? (
              <Alert severity="info" icon={<Assignment />} sx={{ mt: 2 }}>
                No work orders assigned to this workstation.
              </Alert>
            ) : (
              workOrders.map((wo) => (
                <WorkOrderCard
                  key={wo.id}
                  wo={{ ...wo, status: normalizeStatus(wo.status) as WorkOrder['status'] }}
                  isActive={activeWo?.id === wo.id}
                  onClick={() => setActiveWo(wo)}
                />
              ))
            )}
          </Box>
        </Box>

        {/* Active Operation */}
        <Box sx={{ flex: 1, p: 2.5, overflowY: 'auto' }}>
          {!activeWo ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              color="text.secondary"
            >
              <Factory sx={{ fontSize: 96, color: 'action.disabled', mb: 2 }} />
              <Typography variant="h5" color="text.secondary" fontWeight={600}>
                Select a Work Order
              </Typography>
              <Typography variant="body1" color="text.secondary" mt={1}>
                Tap a work order from the queue to get started
              </Typography>
            </Box>
          ) : (
            <ActiveOperation
              wo={activeWo}
              token={session.token}
              onStatusChange={handleStatusChange}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default WorkstationTerminal;
